import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, MapPin, Users, Check } from "lucide-react";
import { api, euro, formatApiError } from "@/lib/api";
import { useLang, trFormation } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Reveal, SectionLabel } from "@/components/Reveal";

export default function FormationDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tr } = useLang();
  const [formation, setFormation] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [sessionRef, setSessionRef] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cgvAccepted, setCgvAccepted] = useState(false);

  useEffect(() => {
    api
      .get(`/formations/${slug}`)
      .then((r) => {
        setFormation(r.data);
        setSessionRef(r.data.sessions?.[0]?.id || null);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const book = async () => {
    if (!formation) return;
    if (!user) {
      navigate("/connexion", { state: { from: `/formations/${slug}` } });
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: formation.lookup_key,
        session_ref: sessionRef,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(formatApiError(e));
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <main className="min-h-screen grid place-items-center bg-abyss px-6">
        <div className="text-center">
          <p className="font-serif text-4xl text-foam">{tr("Cette formation n'existe plus.")}</p>
          <Link to="/formations" data-testid="back-home-link" className="mt-8 inline-block rounded-full bg-aqua px-7 py-3 text-sm font-semibold text-abyss">
            {tr("Toutes les formations")}
          </Link>
        </div>
      </main>
    );
  }

  if (!formation) {
    return (
      <main className="min-h-screen grid place-items-center bg-abyss" data-testid="formation-loading">
        <div className="h-10 w-10 rounded-full border-2 border-aqua/30 border-t-aqua animate-spin" />
      </main>
    );
  }

  const f = trFormation(formation, tr);
  const priced = f.price > 0;

  return (
    <main className="bg-abyss pt-[72px]" data-testid="formation-detail-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={f.image} alt="" className="h-full w-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(3,10,22,0.75)_0%,rgba(3,10,22,0.6)_55%,#030A16_100%)]" />
          <div className="absolute inset-0 caustic-glow" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-16 md:px-10 md:pb-28 md:pt-24">
          <Link
            to="/formations"
            data-testid="back-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-foam/60 hover:text-foam transition-colors"
          >
            <ArrowLeft size={14} /> {tr("Toutes les formations")}
          </Link>
          <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.32em] text-aqua">
            {tr("Module")} {f.module_code} · {f.duration_days} {tr("jours")} · {f.hours} h
          </p>
          <h1 className="mt-4 max-w-3xl font-serif text-5xl md:text-6xl font-light leading-[1.05] text-foam">
            {f.title.split("—")[0].trim()}{" "}
            <em className="italic text-aqua">— {f.title.split("—")[1]?.trim() || f.subtitle}</em>
          </h1>
          <p className="mt-6 max-w-2xl text-sm md:text-base leading-relaxed text-foam/70">
            {f.description}
          </p>
        </div>
      </section>

      <div className="mx-auto grid max-w-7xl gap-14 px-5 pb-28 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <SectionLabel>{tr("Le programme")}</SectionLabel>
            <ul className="mt-8 space-y-0" data-testid="curriculum-list">
              {f.curriculum.map((c, i) => (
                <li key={i} className="flex items-start gap-5 border-b border-aqua/12 py-5 first:border-t">
                  <span className="font-mono text-xs text-aqua/80 pt-1">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-sm md:text-base leading-relaxed text-foam/75">{c}</p>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="glass-card mt-10 rounded-2xl p-7">
              <h3 className="font-serif text-xl text-foam">{tr("Prérequis")}</h3>
              <ul className="mt-4 space-y-2.5 text-sm text-foam/70">
                {[
                  "Avoir reçu au moins une séance de Janzu",
                  "18 ans minimum",
                  "Être physiquement apte à se mouvoir librement",
                ].map((p) => (
                  <li key={p} className="flex items-center gap-2.5">
                    <Check size={14} className="text-aqua shrink-0" /> {tr(p)}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <aside className="glass-card sticky top-24 rounded-2xl p-7" data-testid="booking-card">
              <div className="flex items-end justify-between">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-foam/50">{tr("Tarif du module")}</p>
                  {priced ? (
                    <p className="mt-1 font-serif text-4xl text-aqua" data-testid="price-tag">{euro(f.price)}</p>
                  ) : (
                    <p className="mt-1 font-serif text-3xl italic text-aqua" data-testid="price-tag">{tr("Tarif sur demande")}</p>
                  )}
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-aqua/25 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-foam/70">
                  <Users size={12} /> {tr("6 à 18 places")}
                </span>
              </div>

              <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-aqua">{tr("Choisissez votre session")}</p>
              <div className="mt-4 space-y-3">
                {f.sessions.map((s) => {
                  const active = s.id === sessionRef;
                  const maurice = (s.lieu || "").toLowerCase().includes("maurice");
                  const accent = maurice ? "text-amber-300" : "text-aqua";
                  return (
                    <button
                      key={s.id}
                      data-testid={`session-option-${s.id}`}
                      onClick={() => setSessionRef(s.id)}
                      className={`w-full rounded-xl border p-4 text-left transition-all duration-300 ${
                        active
                          ? maurice
                            ? "border-amber-300/70 bg-amber-300/10 shadow-[0_0_24px_rgba(251,191,36,0.18)]"
                            : "border-aqua bg-aqua/10 shadow-[0_0_24px_rgba(56,189,248,0.2)]"
                          : maurice
                            ? "border-amber-300/30 hover:border-amber-300/60"
                            : "border-aqua/15 hover:border-aqua/40"
                      }`}
                    >
                      <p className="flex items-center gap-2 text-sm font-semibold text-foam">
                        <Calendar size={14} className={accent} />
                        {s.periode}
                      </p>
                      <p className="mt-1.5 flex items-center gap-2 text-xs text-foam/60">
                        <MapPin size={12} className={accent} /> <span className={accent}>{s.lieu}</span>
                      </p>
                      <p className="mt-0.5 pl-[18px] text-xs text-foam/45">
                        {tr("6 à 18 places restantes")}
                      </p>
                    </button>
                  );
                })}
              </div>

              {priced && (
                <label
                  className="mt-6 flex cursor-pointer items-start gap-3"
                  data-testid="cgv-accept-row"
                >
                  <input
                    type="checkbox"
                    checked={cgvAccepted}
                    onChange={(e) => setCgvAccepted(e.target.checked)}
                    data-testid="cgv-checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-aqua"
                  />
                  <span className="text-xs leading-relaxed text-foam/65">
                    {tr("J'ai lu et j'accepte les")}{" "}
                    <a
                      href="/cgv"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-aqua underline underline-offset-4 hover:text-foam transition-colors"
                      data-testid="cgv-checkbox-link"
                    >
                      {tr("conditions générales de vente")}
                    </a>
                  </span>
                </label>
              )}

              {priced && error && (
                <p className="mt-4 rounded-lg border border-red-400/30 bg-red-400/10 px-4 py-3 text-xs text-red-300" data-testid="booking-error">
                  {error}
                </p>
              )}

              {priced ? (
                <button
                  data-testid="book-button"
                  onClick={book}
                  disabled={busy || !sessionRef || (user && !cgvAccepted)}
                  className="group mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-aqua py-4 text-sm font-semibold text-abyss transition-all duration-300 hover:bg-foam hover:shadow-[0_0_36px_rgba(56,189,248,0.5)] disabled:opacity-50"
                >
                  {busy ? (
                    <span className="h-4 w-4 rounded-full border-2 border-abyss/40 border-t-abyss animate-spin" />
                  ) : (
                    <>
                      {user ? tr("Réserver — paiement sécurisé") : tr("Se connecter pour réserver")}
                      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              ) : (
                <div className="mt-7">
                  <p className="text-sm leading-relaxed text-foam/65">
                    {tr("Contactez-nous pour organiser votre participation.")}
                  </p>
                  <Link
                    to="/contact"
                    data-testid="contact-cta"
                    className="group mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-aqua py-4 text-sm font-semibold text-abyss transition-all duration-300 hover:bg-foam hover:shadow-[0_0_36px_rgba(56,189,248,0.5)]"
                  >
                    {tr("Demander le tarif")}
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              )}

              {priced && (
                <>
                  <p className="mt-4 flex items-center justify-center gap-2 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-foam/45">
                    <Clock size={11} /> {tr("Paiement Stripe · Certifiée Qualiopi")}
                  </p>
                  <p className="mt-2 text-center text-[11px] text-foam/45">
                    {tr("En réservant, vous acceptez nos")}{" "}
                    <Link to="/cgv" className="text-aqua underline-offset-4 hover:underline" data-testid="cgv-booking-link">
                      {tr("conditions générales de vente")}
                    </Link>
                    .
                  </p>
                </>
              )}
            </aside>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
